import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const drawerWidth = 240;

const ViewAnnouncement = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');

  // ✅ DATE FIX (DD/MM/YYYY)
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('/');
    if (parts.length !== 3) return '-';
    return `${parts[0]}/${parts[1]}/${parts[2]}`;
  };

  // ================= API =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          'https://wsmsv1.aidigitalsec.com/api/Anouncement/Teacher_Anouncement',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: sessionStorage.getItem('token'),
            },
            body: JSON.stringify({
              teacherId: sessionStorage.getItem('employeeId'),
            }),
          }
        );

        if (!res.ok) throw new Error('API Error');

        const result = await res.json();
        setData(result.list || []);
        setFilteredData(result.list || []);
      } catch {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ================= SEARCH (TITLE ONLY) =================
  useEffect(() => {
    const filtered = data.filter((x) =>
      x.title?.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredData(filtered);
    setPage(0);
  }, [search, data]);

  const paginated = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: 3,
        backgroundColor: '#f4f6f9',
        minHeight: '100vh',
      }}
    >
      {/* ===== HEADER (FIXED AS PER YOUR CODE) ===== */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#d97706',
          mb: 2,
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
          >
            View Announcement
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ===== TABLE CARD ===== */}
      <Card
        sx={{
          borderRadius: 0,
          backgroundColor: '#f5f6f8',
          border: '1px solid #dcdcdc',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent>
          {/* SEARCH BAR */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search topics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 260,
                backgroundColor: '#fff',
                borderRadius: '25px',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '25px' },
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{ borderRadius: 0, border: '1px solid #dcdcdc' }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
                      {[
                        'CLASS',
                        'SECTION',
                        'TITLE',
                        'MESSAGE',
                        'CREATED ON',
                        'CREATED BY',
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 700, border: '1px solid #dcdcdc' }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">
                            No Topics Found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ border: '1px solid #dcdcdc' }}>
                            {row.className || '-'}
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #dcdcdc' }}>
                            {row.sectiionName || '-'}
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #dcdcdc' }}>
                            {row.title}
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #dcdcdc' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setTitle(row.title);
                                setMessage(row.message);
                                setOpen(true);
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #dcdcdc' }}>
                            {formatDate(row.createdOn)}
                          </TableCell>
                          <TableCell sx={{ border: '1px solid #dcdcdc' }}>
                            {row.createdBy}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* PAGINATION */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Typography>Rows:</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(e.target.value);
                    setPage(0);
                  }}
                >
                  {[5, 10, 15, 20, 25].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>

                <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
                  Prev
                </Button>
                <Button
                  disabled={(page + 1) * rowsPerPage >= filteredData.length}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* MESSAGE DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Massage</DialogTitle>
        <Divider />
        <DialogContent>
          <Box
            sx={{
              p: 2,
              border: '1px solid #e0e0e0',
              backgroundColor: '#fafafa',
            }}
            dangerouslySetInnerHTML={{ __html: message }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#d97706' }}
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewAnnouncement;
